import L from 'leaflet';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { CircleMarker, Marker, Polyline, Tooltip } from 'react-leaflet';
import '../../assets/deviceMap.css';
import {
  analyzeChains,
  bearingDeg,
  chainSummary,
  chainTailLabel,
  CONDITION_COLOR,
  gradientSegments,
  hopRows,
  hopsOnMap,
  hopStatusLabel,
  hopTitle,
  interpolate,
  markersOnMap,
  mixHex,
  nodeCondition,
  sharedBranchLabel,
  theoryRows,
  waveRows,
} from '../../utils/floodChain.utils';

/**
 * FloodChainLayer — the waterway chains drawn on the device map, with the
 * flood wave's progress read off the gauges' own data.
 *
 * An experiment (Mike, 2026-08-28): "if more than 2 nodes are connected we
 * could do some time extrapolation that could allow us to calculate the
 * theoretical flow rate and show that on the map, highlight the path".
 *
 * WHAT IS DRAWN, per chain, head to tail (see floodChain.utils):
 *   every hop     a COLOUR GRADIENT from the upstream node's condition to the
 *                 downstream node's — red flooding, yellow warning, green
 *                 normal, grey offline — the same rule as the pins (Mike,
 *                 2026-08-28: "yellow to red, red to green where the event has
 *                 not yet reached"); arrow glyphs at 30 % and 70 % of the hop
 *                 point the way the water flows
 *   passed hops   solid, heavy — the wave has reached both ends
 *   the next hop  heavy, dashed — the water is on its way down it
 *   pending hops  thin dashed
 *   the wave      a blue dot moving along the next hop by elapsed time
 *   the plan      a hollow orange ring where the wave WOULD be now had it left
 *                 the head at the head's arrival and moved at the configured
 *                 speeds — what the simulator's chainWalk runs. Ring and dot
 *                 apart = the water is not moving as configured.
 *   a fork        two branches below one device are two chains sharing the
 *                 hops above it — each shared hop and marker is drawn once
 *
 * NOTHING IS WRITTEN ON THE MAP ITSELF. Gauges sit close enough together that
 * standing labels print over one another and over the pins, so every figure —
 * the ETA at the next gauge, the speed, the plan — waits on the hover, as a
 * short panel of label/value rows.
 *
 * Nothing here is stored or sent. The lines are the straight lines the routing
 * card measured, so they are a schematic of the river, not its course. The
 * clock ticks every 30 s so the dot moves and the ETAs stay honest.
 */

/** Weight and dash say where the wave is; the COLOUR comes from the nodes. */
const STYLE = {
  passed: { weight: 6, opacity: 0.95 },
  next: { weight: 6, opacity: 0.95, dashArray: '10 8' },
  pending: { weight: 3, opacity: 0.85, dashArray: '4 8' },
  unknown: { weight: 3, opacity: 0.6, dashArray: '4 8' },
};

const GRADIENT_STEPS = 8;
const ARROW_AT = [0.3, 0.7];
const TICK_MS = 30_000;

/** A rotated arrow glyph, coloured like the line under it. */
function arrowIcon(bearing, color) {
  return L.divIcon({
    className: 'flood-chain-arrow',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `<div style="width:18px;height:18px;line-height:18px;text-align:center;font-size:16px;color:${color};text-shadow:0 0 2px #000,0 0 2px #000;transform:rotate(${Math.round(bearing - 90)}deg)">➤</div>`,
  });
}

/** The hover: a title, a few label/value rows, and the chain it belongs to. */
function TipPanel({ title, chip, rows, shared, footer }) {
  return (
    <div className="flood-chain-tip__panel">
      <div className="flood-chain-tip__head">
        <span className="flood-chain-tip__title">{title}</span>
        {chip && <span className="flood-chain-tip__chip">{chip}</span>}
      </div>
      <dl className="flood-chain-tip__rows">
        {rows.map((r) => (
          <Fragment key={r.label}>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </Fragment>
        ))}
      </dl>
      {footer && <div className="flood-chain-tip__foot">{footer}</div>}
      {shared && <div className="flood-chain-tip__foot">{shared}</div>}
    </div>
  );
}

export default function FloodChainLayer({ devices }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const analyses = useMemo(() => analyzeChains(devices ?? [], now), [devices, now]);
  // A fork (two branches below one device) is two chains sharing the hops above
  // it; draw each shared hop and marker ONCE (floodChain.utils hopsOnMap / markersOnMap).
  const hops = useMemo(() => hopsOnMap(analyses), [analyses]);
  const waves = useMemo(() => markersOnMap(analyses, (a) => a.wave), [analyses]);
  const rings = useMemo(() => markersOnMap(analyses, (a) => a.theory?.wave), [analyses]);

  if (analyses.length === 0) return null;

  const sharedOf = ({ analysis, branches }) =>
    sharedBranchLabel(branches, chainTailLabel(analysis));

  return (
    <>
      {hops.map(({ analysis, hop, branches }) => {
        if (!hop.fromCoords || !hop.toCoords) return null;
        const key = `${hop.from.id}-${hop.to.id}`;
        const fromColor = CONDITION_COLOR[nodeCondition(hop.from)];
        const toColor = CONDITION_COLOR[nodeCondition(hop.to)];
        const segments = gradientSegments(
          hop.fromCoords,
          hop.toCoords,
          fromColor,
          toColor,
          GRADIENT_STEPS,
        );
        const bearing = bearingDeg(hop.fromCoords, hop.toCoords);
        const tooltip = (
          <Tooltip sticky className="flood-chain-tip">
            <TipPanel
              title={hopTitle(hop)}
              chip={hopStatusLabel(hop)}
              rows={hopRows(hop)}
              footer={chainSummary(analysis)}
              shared={sharedOf({ analysis, branches })}
            />
          </Tooltip>
        );
        return (
          <Fragment key={key}>
            {segments.map((seg, i) => (
              <Polyline
                key={`${key}:${i}`}
                positions={seg.positions}
                pathOptions={{ ...STYLE[hop.status], color: seg.color }}
              >
                {tooltip}
              </Polyline>
            ))}
            {ARROW_AT.map((f) => (
              <Marker
                key={`${key}:arrow:${f}`}
                position={interpolate(hop.fromCoords, hop.toCoords, f)}
                icon={arrowIcon(bearing, mixHex(fromColor, toColor, f))}
                interactive={false}
                zIndexOffset={-500}
              />
            ))}
          </Fragment>
        );
      })}

      {waves.map((marker) => {
        const { analysis, wave } = marker;
        return (
          <CircleMarker
            key={`wave:${wave.hop.from.id}-${wave.hop.to.id}`}
            center={wave.position}
            radius={8}
            pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#1e88e5', fillOpacity: 0.95 }}
          >
            <Tooltip sticky className="flood-chain-tip">
              <TipPanel
                title="Flood wave"
                rows={waveRows(analysis)}
                footer={chainSummary(analysis)}
                shared={sharedOf(marker)}
              />
            </Tooltip>
          </CircleMarker>
        );
      })}

      {rings.map((marker) => {
        const { analysis, wave } = marker;
        const rows = theoryRows(analysis);
        return (
          <CircleMarker
            key={`theory:${wave.hop.from.id}-${wave.hop.to.id}`}
            center={wave.position}
            radius={10}
            pathOptions={{
              color: '#ff9800',
              weight: 3,
              dashArray: '4 3',
              fillColor: '#ff9800',
              fillOpacity: 0.15,
            }}
          >
            <Tooltip sticky className="flood-chain-tip">
              <TipPanel
                title="On the plan"
                rows={rows ?? []}
                footer={chainSummary(analysis)}
                shared={sharedOf(marker)}
              />
            </Tooltip>
          </CircleMarker>
        );
      })}
    </>
  );
}
